import { useEffect } from "react";
import { SEO } from "@/components/seo";

export default function TermsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO
        title="Terms of Service"
        description="Terms of Service for Sierra Chart Pro. Read our terms regarding purchases, licensing, copyright, and usage policies."
        keywords="Sierra Chart Pro terms of service, trading templates license, digital products terms"
        canonical="/terms"
      />

      <h1 className="text-2xl sm:text-3xl font-bold mb-2">Terms of Service</h1>
      <p className="text-muted-foreground mb-8 text-sm">
        Last updated: February 22, 2026
      </p>

      <div className="space-y-8 text-sm leading-relaxed">
        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">1. Agreement to Terms</h2>
          <p className="text-muted-foreground">
            By accessing or using SierraChartPro.com ("the Website") and purchasing any products ("Products"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree with any part of these Terms, you must not use the Website or purchase any Products. These Terms constitute a legally binding agreement between you ("User," "you," or "your") and Sierra Chart Pro ("we," "us," or "our").
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">2. Products & Digital Downloads</h2>
          <p className="text-muted-foreground mb-3">
            Sierra Chart Pro sells digital products including, but not limited to, chart templates, custom indicators, and trading tools designed for use with the Sierra Chart platform. All Products are delivered electronically as downloadable files upon completed purchase.
          </p>
          <p className="text-muted-foreground">
            We make no guarantees regarding trading performance or financial results from using our Products. Our Products are tools for market analysis and are not financial advice. Trading involves substantial risk of loss and is not suitable for all investors.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">3. Single-User License</h2>
          <div className="rounded-lg border border-border bg-card p-4 mb-3">
            <p className="text-foreground font-medium mb-2">Important: All Products are licensed for single-user use only.</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>Each purchase grants a license to <span className="text-foreground font-medium">one individual user only</span>.</li>
              <li>Products may <span className="text-foreground font-medium">not be shared, distributed, transferred, or resold</span> to any other person, entity, or organization.</li>
              <li>You may install and use the Products on multiple computers or devices that you personally own and operate, but only for your own individual use.</li>
              <li>Sharing your account credentials or download links with others is strictly prohibited.</li>
              <li>Group licenses, team licenses, or multi-user licenses must be purchased separately by contacting us directly.</li>
            </ul>
          </div>
          <p className="text-muted-foreground">
            Violation of the single-user license terms may result in immediate termination of your license, revocation of access to all purchased Products, and legal action to recover damages.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">4. Copyright & Intellectual Property</h2>
          <div className="rounded-lg border border-border bg-card p-4 mb-3">
            <p className="text-foreground font-medium mb-2">All Products are protected by copyright law.</p>
            <ul className="list-disc list-inside space-y-2 text-muted-foreground">
              <li>All chart templates, indicators, source code, documentation, images, and related materials are the <span className="text-foreground font-medium">exclusive intellectual property of Sierra Chart Pro</span> and are protected under United States and international copyright laws.</li>
              <li>Purchasing a Product grants you a <span className="text-foreground font-medium">limited, non-exclusive, non-transferable license</span> to use the Product for personal trading purposes only.</li>
              <li>You do <span className="text-foreground font-medium">not</span> acquire any ownership rights to the underlying intellectual property.</li>
              <li><span className="text-foreground font-medium">Reverse engineering, decompiling, disassembling, or attempting to derive the source code</span> of any indicator or compiled Product is strictly prohibited.</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">5. Prohibited Uses</h2>
          <p className="text-muted-foreground mb-3">You may not:</p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Copy, reproduce, duplicate, or redistribute any Product in whole or in part.</li>
            <li>Share, upload, or post Products on any website, forum, file-sharing service, cloud storage, or social media platform.</li>
            <li>Sell, resell, sublicense, rent, lease, or lend any Product to third parties.</li>
            <li>Modify, adapt, or create derivative works based on our Products for the purpose of distribution or resale.</li>
            <li>Remove, alter, or obscure any copyright notices, watermarks, or proprietary markings on any Product.</li>
            <li>Use any Product in a commercial training, education, or signal service without explicit written permission from Sierra Chart Pro.</li>
            <li>Claim authorship or ownership of any Product or present it as your own work.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">6. Purchases & Payment</h2>
          <p className="text-muted-foreground mb-3">
            All purchases are processed securely through Stripe. By making a purchase, you agree that:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>All prices are listed in US Dollars (USD) and are subject to change without notice.</li>
            <li>Payment is required in full at the time of purchase before access to download links is granted.</li>
            <li>You are responsible for any applicable taxes in your jurisdiction.</li>
            <li>You must provide accurate and complete billing information.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">7. Refund Policy</h2>
          <p className="text-muted-foreground mb-3">
            Due to the digital nature of our Products, <span className="text-foreground font-medium">all sales are generally final</span>. Once a Product has been downloaded, we cannot verify whether it has been copied or retained.
          </p>
          <p className="text-muted-foreground mb-3">
            However, we may issue a refund at our sole discretion in the following cases:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>The Product is genuinely defective and we are unable to provide a fix within a reasonable timeframe.</li>
            <li>A duplicate purchase was made in error (same Product purchased twice by the same user).</li>
            <li>The Product was not delivered due to a technical error on our end.</li>
          </ul>
          <p className="text-muted-foreground mt-3">
            Refund requests must be submitted within 7 days of purchase. We reserve the right to deny refund requests that do not meet the above criteria.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">8. Account Responsibilities</h2>
          <p className="text-muted-foreground">
            You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You must notify us immediately of any unauthorized use of your account. We reserve the right to suspend or terminate accounts that violate these Terms or are used for fraudulent purposes.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">9. Copyright Infringement & Enforcement</h2>
          <p className="text-muted-foreground mb-3">
            We take copyright infringement seriously. If we discover or are made aware that our Products are being shared, redistributed, or used in violation of these Terms, we reserve the right to:
          </p>
          <ul className="list-disc list-inside space-y-2 text-muted-foreground">
            <li>Immediately terminate the violating user's account and revoke access to all Products.</li>
            <li>Pursue legal action and seek damages, including but not limited to statutory damages, actual damages, lost profits, and attorney's fees.</li>
            <li>Issue DMCA takedown notices to any platform hosting unauthorized copies of our Products.</li>
            <li>Report violations to relevant law enforcement authorities.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">10. Disclaimer of Warranties</h2>
          <p className="text-muted-foreground">
            Products are provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that our Products will meet your specific requirements, that they will be error-free, or that they will produce any particular trading results. We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">11. Limitation of Liability</h2>
          <p className="text-muted-foreground">
            In no event shall Sierra Chart Pro, its owners, employees, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, trading losses, data loss, or other intangible losses, resulting from your use or inability to use our Products, even if we have been advised of the possibility of such damages. Our total liability for any claim arising from these Terms or your use of our Products shall not exceed the amount you paid for the specific Product giving rise to the claim.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">12. Modifications to Terms</h2>
          <p className="text-muted-foreground">
            We reserve the right to update or modify these Terms at any time without prior notice. Changes will be effective immediately upon posting to the Website. Your continued use of the Website or Products after any changes constitutes your acceptance of the revised Terms. We encourage you to review these Terms periodically.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">13. Governing Law</h2>
          <p className="text-muted-foreground">
            These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions. Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts in the United States.
          </p>
        </section>

        <section>
          <h2 className="text-lg font-semibold text-primary mb-3">14. Contact Information</h2>
          <p className="text-muted-foreground">
            If you have questions about these Terms of Service, please contact us at support@sierrachartpro.com.
          </p>
        </section>
      </div>
    </div>
  );
}
